import { createSlice } from '@reduxjs/toolkit';
import { logoutAction } from 'features/common/commonSlice';
import {
  getExpedientDetailAsync,
  getExpedientLiveAlertsAsync,
  getExpedientQueueAsync,
  postHandleExpedientAsync,
  postSplitExpedientAsync,
  resolveExpedientAsync,
  holdExpedientAsync,
} from './expedientQueueThunks';
import { ExpedientQueueItem } from './expedientQueueTypes';

// Feature expedientes (F5). Estado aislado del alarmQueue clásico: con el flag apagado
// este slice queda inerte y el flujo por alerta sigue intacto.

export interface ExpedientQueueState {
  expedients: ExpedientQueueItem[];
  totalSize: number;
  requestSize: number;
  order: 'asc' | 'desc';
  status: 'idle' | 'loading' | 'failed';
  /** Ids de alertas ya vistas en polls anteriores: base del beep/pulso por alerta (D4/D4b). */
  seenAlertIds: string[];
  /** El primer poll de la sesión ya sembró seenAlertIds (aunque llegase vacío). */
  seeded: boolean;
  /** Alertas nuevas detectadas en el último poll (dispara beep + pulso; se consume al sonar). */
  arrivedAlertIds: string[];
  /** Expedientes con alguna alerta nueva en el último poll (pulso visual de la card). */
  pulsedExpedientIds: string[];
  /** Expediente en atención (item de la cola v2). */
  selectedExpedient: ExpedientQueueItem | null;
  /** Detalle persistente de gdp (eventos miembros + resolución). */
  detail: any | null;
  detailStatus: 'idle' | 'loading' | 'failed';
  /** Hijo de los splits de la atención en curso (se reutiliza en splits repetidos, D9). */
  splitChildId: string | null;
  openExpedientAttention: boolean;
  /** Estado vivo (SAC) del expediente en atención: sus alertas vivas con estado. Fuente del timeline. */
  liveAlerts: ExpedientLiveAlerts;
  /** Ids vistos por el estado vivo: beep también de lo que no cabe en la página de la cola. */
  liveSeenAlertIds: string[];
}

export interface ExpedientLiveAlerts {
  expedientId: string | null;
  alerts: any[];
  fetchedAt: number | null;
}

const EMPTY_LIVE_ALERTS: ExpedientLiveAlerts = { expedientId: null, alerts: [], fetchedAt: null };

const initialState: ExpedientQueueState = {
  expedients: [],
  totalSize: 0,
  requestSize: 10,
  order: 'desc',
  status: 'idle',
  seenAlertIds: [],
  seeded: false,
  arrivedAlertIds: [],
  pulsedExpedientIds: [],
  selectedExpedient: null,
  detail: null,
  detailStatus: 'idle',
  splitChildId: null,
  openExpedientAttention: false,
  liveAlerts: EMPTY_LIVE_ALERTS,
  liveSeenAlertIds: [],
};

const collectAlertIds = (expedients: ExpedientQueueItem[]): string[] => {
  const ids: string[] = [];
  expedients.forEach((expedient) => {
    (expedient.alerts ?? []).forEach((alert: any) => {
      const id = alert?.uuid ?? alert?.id;
      if (id) ids.push(String(id));
    });
  });
  return ids;
};

/** Resolver o dejar en espera: el expediente sale de la atención. */
const clearAttention = (state: any) => {
  state.selectedExpedient = null;
  state.openExpedientAttention = false;
  state.detail = null;
  state.splitChildId = null;
  state.liveAlerts = EMPTY_LIVE_ALERTS;
};

export const expedientQueueSlice = createSlice({
  name: 'expedientQueue',
  initialState,
  reducers: {
    setExpedientQueueSize: (state, action) => {
      state.requestSize = action.payload;
    },
    setExpedientQueueOrder: (state, action) => {
      state.order = action.payload;
    },
    selectExpedient: (state, action) => {
      state.selectedExpedient = action.payload;
      state.openExpedientAttention = !!action.payload;
      state.splitChildId = null;
      if (action.payload?.expedientId !== state.liveAlerts.expedientId) state.liveAlerts = EMPTY_LIVE_ALERTS;
      if (!action.payload) {
        state.detail = null;
        state.detailStatus = 'idle';
      }
    },
    /** El beep/pulso ya se emitió: no re-sonar con el mismo poll. */
    consumeArrivedAlerts: (state) => {
      state.arrivedAlertIds = [];
      state.pulsedExpedientIds = [];
    },
    clearExpedientAttention: (state) => {
      state.selectedExpedient = null;
      state.openExpedientAttention = false;
      state.detail = null;
      state.detailStatus = 'idle';
      state.splitChildId = null;
      state.liveAlerts = EMPTY_LIVE_ALERTS;
    },
  },
  extraReducers: (builder) => {
    builder
      // El logout no recarga la página: otro operador heredaría la semilla del beep.
      .addCase(logoutAction, () => initialState)
      .addCase(getExpedientQueueAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getExpedientQueueAsync.fulfilled, (state, action: any) => {
        state.status = 'idle';

        const expedients: ExpedientQueueItem[] = action.payload.expedients ?? [];
        const incomingIds = collectAlertIds(expedients);

        // Beep por alerta nueva (D4): el disparador es la alerta, no el contador de
        // cards — un expediente existente que recibe otra alerta también suena.
        // El primer poll de la sesión solo siembra (no sonar por el backlog). Va por flag
        // y no por seenAlertIds.length: con la cola vacía la primera alerta no sonaba.
        if (state.seeded) {
          // También lo visto por el estado vivo del expediente en atención: una alerta que
          // sonó por esa vía no vuelve a sonar cuando entra en la página de la cola.
          const seen = new Set([...state.seenAlertIds, ...state.liveSeenAlertIds]);
          const arrived = incomingIds.filter((id) => !seen.has(id));
          if (arrived.length > 0) {
            state.arrivedAlertIds = arrived;
            const arrivedSet = new Set(arrived);
            state.pulsedExpedientIds = expedients
              .filter((expedient) =>
                (expedient.alerts ?? []).some((alert: any) => arrivedSet.has(String(alert?.uuid ?? alert?.id))))
              .map((expedient) => expedient.expedientId);
          }
        }
        state.seenAlertIds = incomingIds;
        state.seeded = true;

        state.expedients = expedients;
        state.totalSize = action.payload.totalSize ?? 0;

        // Si el expediente en atención sigue en cola (p.ej. tras un park), refrescar su
        // item para que la vista vea las alertas nuevas que hayan caído (I2/I3).
        if (state.selectedExpedient) {
          const refreshed = expedients.find((expedient) => expedient.expedientId === state.selectedExpedient?.expedientId);
          if (refreshed) state.selectedExpedient = refreshed;
        }
      })
      .addCase(getExpedientQueueAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(postHandleExpedientAsync.fulfilled, (state, action: any) => {
        if (action.payload?.data?.ok) {
          state.openExpedientAttention = true;
        }
      })
      .addCase(getExpedientLiveAlertsAsync.fulfilled, (state, action: any) => {
        const { expedientId, alerts, fetchedAt } = action.payload ?? {};
        const liveAlerts: any[] = alerts ?? [];
        state.liveAlerts = { expedientId: expedientId ?? null, alerts: liveAlerts, fetchedAt: fetchedAt ?? null };

        // Beep (D4) también por esta vía: una pendiente nueva del expediente en atención puede
        // no caber en la página de la cola (10 por prioridad) y entonces la campana no la ve.
        const liveIds = liveAlerts.map((alert: any) => String(alert?.alertId ?? '')).filter(Boolean);
        if (state.seeded) {
          const seen = new Set([...state.seenAlertIds, ...state.liveSeenAlertIds]);
          const arrived = liveAlerts
            .filter((alert: any) => alert?.state === 'PENDING_HANDLE' && !seen.has(String(alert?.alertId)))
            .map((alert: any) => String(alert.alertId));
          if (arrived.length > 0) {
            state.arrivedAlertIds = [...state.arrivedAlertIds, ...arrived];
            if (expedientId && !state.pulsedExpedientIds.includes(expedientId)) state.pulsedExpedientIds.push(expedientId);
          }
        }
        state.liveSeenAlertIds = liveIds;
      })
      .addCase(getExpedientDetailAsync.pending, (state) => {
        state.detailStatus = 'loading';
      })
      .addCase(getExpedientDetailAsync.fulfilled, (state, action: any) => {
        state.detailStatus = 'idle';
        state.detail = action.payload;
      })
      .addCase(getExpedientDetailAsync.rejected, (state) => {
        state.detailStatus = 'failed';
      })
      .addCase(postSplitExpedientAsync.fulfilled, (state, action: any) => {
        // Splits siguientes de la misma atención acumulan sobre el mismo hijo (D9).
        state.splitChildId = action.payload?.childExpedientId ?? state.splitChildId;

        // El expediente seleccionado no vive en la cola general durante la atención
        // (sin pendientes), así que el poll no lo refresca: se le quitan aquí las
        // alertas movidas para que el timeline no las siga pintando como vivas.
        const movedIds: string[] = action.meta?.arg?.alertIds ?? [];
        if (state.selectedExpedient?.alerts && movedIds.length > 0) {
          state.selectedExpedient.alerts = state.selectedExpedient.alerts.filter(
            (alert: any) => !movedIds.includes(String(alert?.uuid ?? alert?.id)),
          );
        }
      })
      .addCase(resolveExpedientAsync.fulfilled, clearAttention)
      // Dejar en espera también saca el expediente de la atención (sale de la cola del SAC).
      .addCase(holdExpedientAsync.fulfilled, clearAttention);
  },
});

export const {
  setExpedientQueueSize,
  setExpedientQueueOrder,
  selectExpedient,
  consumeArrivedAlerts,
  clearExpedientAttention,
} = expedientQueueSlice.actions;

export default expedientQueueSlice.reducer;
